import React, { useState } from 'react';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { shadows } from '../theme/shadows';

interface RadioCardProps {
    title: string;
    description?: string;
    selected: boolean;
    onSelect: () => void;
    disabled?: boolean;
}

export const RadioCard: React.FC<RadioCardProps> = ({
    title,
    description,
    selected,
    onSelect,
    disabled = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        // Motion: Separate transitions for border (120ms) and background/shadow (150ms)
        transition: 'border 120ms ease-out, background-color 150ms ease-out, box-shadow 150ms ease-out',
        outline: 'none',
        boxSizing: 'border-box',
        width: '100%',
        position: 'relative',

        // Refinement: Dynamic Background with warm tint for selected
        backgroundColor: selected ? '#FFFBF5' : '#FFFFFF',

        // Refinement: Dynamic Border with Brass left accent
        border: selected
            ? `2px solid ${colors.primary.navy}`
            : `1px solid ${isHovered && !disabled ? colors.primary.navy : '#E5E7EB'}`,
        // Brass left accent for selected
        borderLeft: selected ? '4px solid #C9B37E' : undefined,

        // Refinement: Elevation with stronger shadow for selected
        boxShadow: selected
            ? '0 4px 12px -2px rgba(0, 0, 0, 0.12), 0 2px 6px -1px rgba(0, 0, 0, 0.08)'
            : shadows.subtle,

        // Refinement: Focus Ring (Accessibility)
        ...(isFocused ? { boxShadow: `0 0 0 2px ${colors.background.offWhite}, 0 0 0 4px ${colors.primary.navy}` } : {}),
    };

    // Calculate padding to prevent layout shift between 1px and 2px border
    // Standard padding is 16px.
    // If selected (2px border), padding is 16px.
    // If unselected (1px border), padding is 17px to compensate.
    const paddingVal = selected ? spacing[16] : `calc(${spacing[16]} + 1px)`;

    const adjustedStyle = {
        ...containerStyle,
        padding: paddingVal,
    }

    const radioIndicatorStyle: React.CSSProperties = {
        flexShrink: 0,
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        // Refinement: Indicator border matches the theme intensity
        border: `2px solid ${selected || (isHovered && !disabled) ? colors.primary.navy : '#D1D5DB'}`,
        marginRight: spacing[12],
        marginTop: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 120ms ease-out',
        backgroundColor: '#FFFFFF', // Ensure indicator is always white-filled
    };

    const radioInnerStyle: React.CSSProperties = {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: colors.primary.navy,
        opacity: selected ? 1 : 0,
        transform: selected ? 'scale(1)' : 'scale(0.5)',
        transition: 'opacity 150ms ease-out, transform 150ms ease-out',
    };

    const contentStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
    };

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        fontWeight: typography.weights.medium,
        // Refinement: Title gets Navy color when selected (Stronger contrast)
        color: selected ? colors.primary.navy : colors.text.primary,
        marginBottom: description ? spacing[4] : 0,
        lineHeight: typography.sizes.body.lineHeight,
        transition: 'color 150ms ease-out',
    };

    const descriptionStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.regular,
        color: colors.text.muted,
        lineHeight: typography.sizes.small.lineHeight,
    };

    return (
        <div
            role="radio"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onSelect()}
            onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onSelect();
                }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={adjustedStyle}
        >
            <div style={radioIndicatorStyle}>
                <div style={radioInnerStyle} />
            </div>
            <div style={contentStyle}>
                <span style={titleStyle}>{title}</span>
                {description && <span style={descriptionStyle}>{description}</span>}
            </div>
        </div>
    );
};
